import React, { useState } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  Typography, 
  Alert, 
  CircularProgress, 
  Collapse, 
  IconButton, 
  Chip 
} from '@mui/material';
import { 
  TrendingUp as PulseIcon, 
  ExpandMore as ExpandMoreIcon, 
  ExpandLess as ExpandLessIcon, 
  Info as InfoIcon 
} from '@mui/icons-material';
import { functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';

const StrategicPulse = ({ clientId, currentNarrative, onUpdate, clientIsActive, user, userData }) => {
    const [updateText, setUpdateText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);

    const handleSubmit = async () => {
        if (!updateText.trim()) {
            toast.error('Please enter an update.');
            return;
        }
        if (!clientId || clientId === 'ALL') {
            toast.error('Please select a specific client to update.');
            return;
        }

        setIsSubmitting(true);
        const toastId = toast.loading('Updating strategic context...');

        try {
            const updateNarrativeFunction = httpsCallable(functions, 'updateClientStrategicNarrative');
            const result = await updateNarrativeFunction({ 
              clientId, 
              updateText: updateText.trim() 
            });
            
            if (result.data.success) {
                toast.update(toastId, {
                    render: 'Strategic context updated successfully!',
                    type: 'success',
                    isLoading: false,
                    autoClose: 3000
                });

                // --- Phase 5: Persist Strategic Pulse to clientReports ---
                try {
                  const inputText = updateText.trim();
                  await addDoc(collection(db, 'clientReports'), {
                    clientId,
                    fcfoId: user?.uid,
                    reportType: 'strategic_pulse',
                    title: 'Strategic Pulse Update',
                    content: inputText,
                    contentPreview: inputText.substring(0, 200),
                    generatedAt: serverTimestamp(),
                    generatedBy: user?.uid,
                    organizationId: userData?.organizationId || null,
                    provider: null,  // User-entered, not AI-generated
                    topicTags: []
                  });
                } catch (persistError) {
                  console.error('[Phase5] Failed to persist Strategic Pulse:', persistError);
                }
                // --- End Phase 5 ---

                setUpdateText('');
                if (onUpdate) onUpdate();
            } else {
                throw new Error(result.data.message || 'An unknown error occurred.');
            }
        } catch (error) {
            console.error('Error updating strategic narrative:', error);
            toast.update(toastId, {
                render: `Failed to update: ${error.message}`,
                type: 'error',
                isLoading: false,
                autoClose: 5000
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleSubmit();
        }
    };

    const isDisabled = isSubmitting || !clientId || clientId === 'ALL' || !clientIsActive;

    return (
    <Card
    elevation={2}
    sx={{
        mt: 3,
        mb: 3,
        border: '2px solid',
        borderColor: 'grey.300',
        // The background color is now removed to use the default theme color
    }}
>
        <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                    {/* Icon and Chip have been removed */}
                    <Typography variant="h6" fontWeight={600} color={isDisabled ? 'text.disabled' : 'inherit'}>
                        Strategic Pulse
                    </Typography>
                </Box>
                <Box>
                    <IconButton
                        size="small"
                        onClick={() => setShowInfo(!showInfo)}
                        sx={{ mr: 1 }}
                    >
                        <InfoIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>
            </Box>

            <Collapse in={showInfo}>
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    onClose={() => setShowInfo(false)}
                >
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Keep your AI advisor current with quick strategic updates.</strong>
                    </Typography>
                    <Typography variant="body2">
                        Add 1-3 sentence updates about shifts in priorities, new initiatives, key initiatives that have been implemented, or
                        strategic pivots. Your updates are intelligently integrated by AI,
                        automatically handling conflicts and maintaining context.
                    </Typography>
                </Alert>
            </Collapse>

            <Collapse in={isExpanded}>
                <Box>
                    <TextField
                        multiline
                        rows={3}
                        fullWidth
                        placeholder={`Example: "We're now prioritizing APAC expansion over domestic growth for Q4"`}
                        value={updateText}
                        onChange={(e) => setUpdateText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isDisabled}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white'
                            }
                        }}
                        helperText={
                            !clientIsActive && clientId !== 'ALL'
                                ? 'Client is inactive. Updates are disabled.'
                                : clientId === 'ALL'
                                    ? 'Please select a specific client to add strategic updates.'
                                    : 'Ctrl+Enter to submit • AI-powered integration'
                        }
                    />
                    <Button
    variant="contained"
    fullWidth
    onClick={handleSubmit}
    disabled={!updateText.trim() || isDisabled}
    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <PulseIcon />}
    sx={{ py: 1.5, fontWeight: 600, fontSize: '1rem' }} // Add this line for consistent sizing
>
    {isSubmitting ? 'Updating...' : 'Update Strategic Context'}
</Button>
                </Box>
            </Collapse>
        </CardContent>
    </Card>
);
};

export default StrategicPulse;